import { ButtonHierarchy, ButtonSize, MainButton } from "@beydesign/storybook"
import { Box } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AgentVideo } from "../../components/AgentView/AgentVideo"
import { CardScrim } from "../../components/CallChrome/CardScrim"
import Feedback from "../../components/Feedback/Feedback"
import { CallShell } from "../../components/layout/CallShell"
import { PostScreenLoading } from "../../components/PostScreenLoading"
import { useBranding } from "../../hooks"
import { AnalyticsEvents } from "../../types/Analytics"
import { CallDisconnect, CallDisconnectType } from "../../types/Call"
import { StateAction } from "../../types/State"
import { safeHttpUrl } from "../../utils/branding"
import { isInIframe } from "../../utils/func"
import { getAgent } from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"

export function PostScreen() {
  const { id: agentId } = useParams()
  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const isEmbedded = isInIframe()
  const agent = context.agent
  const branding = useBranding()

  const [isDataReady, setIsDataReady] = useState(false)
  const [headline, setHeadline] = useState("You left the meeting")
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const navigate = useNavigate()
  const callDisconnectType = useLocation()?.state as CallDisconnectType | null
  const callDisconnect = callDisconnectType
    ? CallDisconnect[callDisconnectType]
    : null

  const { logAnalyticsEvent } = useMixpanel()

  const isDemoAgent = useMemo(
    () => agent?.owner?.type === "demo",
    [agent?.owner?.type],
  )

  const customButton = useMemo(() => {
    const url = safeHttpUrl(branding?.brand_button_url)
    if (branding?.brand_button_text && url) {
      return { text: branding.brand_button_text, url }
    }
    return null
  }, [branding])

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        if (agentId) {
          const fetchedAgent = await getAgent(agentId)
          dispatch({ type: StateAction.setAgent, payload: fetchedAgent })
        }
      } catch (error) {
        console.error("Error fetching agent:", error)
      } finally {
        setIsDataReady(true)
      }
    }
    fetchAgent()
  }, [agentId, dispatch])

  const handleCustomButtonClick = () => {
    if (!customButton) return
    window.open(customButton.url, "_self")
    setHeadline(
      isDemoAgent ? "Your demo has ended!" : "Your meeting has ended!",
    )
  }

  const handleGetStartedForFree = () => {
    window.open("https://bey.studio/", "_blank")
    logAnalyticsEvent(AnalyticsEvents.AgentVisited)
  }

  const rejoin = () => {
    logAnalyticsEvent(AnalyticsEvents.Rejoined)
    navigate(`/${agentId}`)
  }

  if (!isDataReady) {
    return <PostScreenLoading isEmbed={isEmbedded} />
  }

  return (
    <CallShell isEmbed={isEmbedded}>
      <AgentVideo agent={agent} />
      <CardScrim>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: {
              xs: "var(--spacing-tokens-size-in-px-spacing-spacing-3xl)",
              sm: "var(--spacing-tokens-size-in-px-spacing-spacing-5xl)",
            },
            width: "100%",
            maxWidth: 696,
            maxHeight: "100%",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--spacing-tokens-size-in-px-spacing-spacing-3xl)",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-tokens-size-in-px-spacing-spacing-xs)",
                fontFamily: "var(--app-font-family, figtree, sans-serif)",
                color: "var(--color-text-text-white)",
              }}
            >
              <Box
                data-testid="post-call-headline"
                sx={{
                  fontSize: "var(--global-heading-l-medium-font-size)",
                  fontWeight: "var(--global-heading-l-medium-font-weight)",
                  lineHeight:
                    "calc(var(--global-heading-l-medium-line-height) * 1px)",
                }}
              >
                {callDisconnect?.reason ||
                  (isDemoAgent ? "Your demo has ended!" : headline)}
              </Box>
              {isDemoAgent && (
                <Box
                  sx={{
                    fontSize: "var(--global-text-s-regular-font-size)",
                    lineHeight:
                      "calc(var(--global-text-s-regular-line-height) * 1px)",
                  }}
                >
                  Did you enjoy your call? Signup to create your own Agent.
                </Box>
              )}
              {callDisconnect?.description && (
                <Box
                  sx={{
                    fontSize: "var(--global-text-s-regular-font-size)",
                    lineHeight:
                      "calc(var(--global-text-s-regular-line-height) * 1px)",
                  }}
                >
                  {callDisconnect.description}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "stretch",
                justifyContent: "center",
                gap: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
                width: "100%",
                maxWidth: 408,
                "& > *": { flex: 1 },
              }}
            >
              <MainButton
                onClick={rejoin}
                disabled={!agentId}
                leadingIcon="VideoCamera"
                showLeadingIcon
                size={ButtonSize.sm}
                text="Restart Conversation"
                hierarchy={ButtonHierarchy.SecondaryColor}
                id="call-postscreen-restart"
              />
              {customButton ? (
                <MainButton
                  onClick={handleCustomButtonClick}
                  size={ButtonSize.sm}
                  text={customButton.text}
                  hierarchy={ButtonHierarchy.PrimaryLemon}
                  id="call-postscreen-custom-cta"
                />
              ) : (
                <MainButton
                  onClick={handleGetStartedForFree}
                  size={ButtonSize.sm}
                  text="Get Started for Free"
                  hierarchy={ButtonHierarchy.PrimaryLemon}
                  id="call-postscreen-get-started"
                />
              )}
            </Box>
          </Box>

          {!feedbackSubmitted && (
            <Feedback
              visible={feedbackOpen}
              onClose={() => setFeedbackOpen(false)}
              onOpen={() => setFeedbackOpen(true)}
              onSubmit={() => setFeedbackSubmitted(true)}
            />
          )}
        </Box>
      </CardScrim>
    </CallShell>
  )
}
